import React, { ReactNode, ReactElement } from 'react';
import { List, Box, Collapse, IconButton, ListItem as MuiListItem, ListItemText, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';


export interface ListItemProps {
    title: string;
    subtitle?: string;
    badge?: string | number;
    icon?: ReactNode;
    onClick?: () => void;
    component?: () => ReactNode;
    selected?: boolean;
    disabled?: boolean;
    children?: ReactNode;
    expanded?: boolean;
    sx?: any;
    iconAction?: 'toggle' | 'custom';
    onIconClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    badgeLoading?: boolean;
    textColor?: string;
    selectedTextColor?: string;
    selectedBackgroundColor?: string;
    hoverBackgroundColor?: string;
    badgeTextColor?: string;
}

/**
 * ListItem component - renders a list item with icon, title, badge, and optional children
 */
export function ListItem(props: ListItemProps): ReactElement {
    const theme = useTheme();
    const {
        title,
        subtitle,
        badge,
        icon,
        onClick,
        component,
        selected = false,
        disabled = false,
        children,
        expanded = false,
        sx,
        onIconClick,
        badgeLoading = false,
        textColor = '#FFFFFF',
        selectedTextColor = '#FFA726',
        selectedBackgroundColor = 'rgba(255, 255, 255, 0.08)',
        hoverBackgroundColor = 'rgba(255, 255, 255, 0.04)',
        badgeTextColor = 'rgba(255, 255, 255, 0.6)',
    } = props;

    if (component) {
        return <Box key={title}>{component()}</Box>;
    }

    const hasChildren = children !== undefined && children !== null;
    const computedTextColor = selected && selectedTextColor ? selectedTextColor : textColor;

    const handleIconClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (onIconClick) {
            onIconClick(event);
        }
    };

    const handleItemClick = () => {
        if (onClick) {
            onClick();
        }
    };

    return (
        <>
            <MuiListItem
                onClick={handleItemClick}
                disabled={disabled}
                disableGutters
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: selected ? selectedBackgroundColor : 'transparent',
                    cursor: disabled ? 'default' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                    padding: '16px',
                    minHeight: 'unset',
                    '&:hover': disabled ? {} : {
                        backgroundColor: hoverBackgroundColor,
                    },
                    ...sx
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    {icon && (
                        <IconButton
                            onClick={handleIconClick}
                            size="small"
                            disabled={disabled}
                            sx={{
                                marginRight: '8px',
                                padding: 0,
                                minWidth: '20px',
                                flexShrink: 0,
                            }}
                        >
                            {icon}
                        </IconButton>
                    )}
                    <ListItemText
                        primary={title}
                        secondary={subtitle}
                        sx={{
                            margin: 0,
                            overflow: 'hidden',
                        }}
                        primaryTypographyProps={{
                            variant: 'body2',
                            style: {
                                color: computedTextColor,
                                margin: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }
                        }}
                        secondaryTypographyProps={{
                            variant: 'caption',
                            style: {
                                color: 'rgba(255, 255, 255, 0.6)',
                                margin: 0,
                                fontSize: '12px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }
                        }}
                    />
                </Box>

                {(badge !== undefined || badgeLoading) && (
                    <Box 
                        sx={{ 
                            marginLeft: '8px', 
                            flexShrink: 0,
                            border: '1px solid rgba(107, 141, 153, 0.5)', 
                            borderRadius: '6px', 
                            padding: '4px 10px', 
                            minWidth: 'fit-content',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '24px', 
                        }}
                    >
                        {badgeLoading ? (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#FFFFFF', 
                                    margin: 0,
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    lineHeight: 1,
                                }}
                            >
                                ...
                            </Typography>
                        ) : (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#FFFFFF', 
                                    margin: 0,
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    lineHeight: 1,
                                }}
                            >
                                {badge}
                            </Typography>
                        )}
                    </Box>
                )}
            </MuiListItem>

            {hasChildren && (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {children}
                    </List>
                </Collapse>
            )}
        </>
    );
}